import clsx from 'clsx'
import {
  ChevronUpIcon,
  InfoIcon,
  LightbulbIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'
import { useMemo } from 'react'

import { useIntercomLauncherOffset } from 'components/reusable/intercom/IntercomLauncher'
import { membersMarketplace } from 'rails_routes'
import { Banner, SolidAlertCircleIcon, TextLink, Tooltip } from 'slab'
import { type GrapeMoney } from 'types'
import { formatMoney, getFormattedDate } from 'utils'

import { BottomSheet } from '../../BottomSheet'
import { useOnboarding } from '../OnboardingContext'
import { type OnboardingEnrollment, type Step } from '../types'
import { calculateTieredAllowanceBreakdown, HSA_SUPPORT_URL } from '../utils'
import { AnimatedCurrency } from './AnimatedCurrency'
import EnrollmentSidebarCard from './EnrollmentSidebarCard'
import { TIERED_ALLOWANCE_TOOLTIP_TEXT } from './PlanCard/Premium'
import { ProviderInNetworkVerificationProgressCard } from './ProviderInNetworkVerificationProgressCard/ProviderInNetworkVerificationProgressCard'

export const calculateRemainingBudget = (
  allowanceAmount: GrapeMoney | undefined | null,
  enrollments: Array<{ quoted_premium?: GrapeMoney | number | undefined }>
) => {
  let remainingBudgetSubunits = allowanceAmount?.subunits || 0
  if (allowanceAmount) {
    const enrollmentPremiumSubunits = enrollments.reduce((acc, enrollment) => {
      const premium = enrollment.quoted_premium as GrapeMoney
      return acc + (premium?.subunits || 0)
    }, 0)
    remainingBudgetSubunits =
      remainingBudgetSubunits - enrollmentPremiumSubunits
  }
  return {
    subunits: remainingBudgetSubunits,
    rawAmount: remainingBudgetSubunits / 100,
    formattedAmount: formatMoney(remainingBudgetSubunits / 100),
  }
}

const calculateTotalPlanCosts = (enrollmentItems: OnboardingEnrollment[]) => {
  return enrollmentItems.reduce((acc, enrollment) => {
    const premium = enrollment.quoted_premium as GrapeMoney
    return acc + (premium?.subunits || 0)
  }, 0)
}

type QmeCheckoutBannerType = 'THATCH_BALANCE' | 'MEDICARE_THATCH_BALANCE' | 'HSA_WARNING'

// Can use QME && atFinalizestep
const QmeCheckoutBanner = (
  { hasMedicalPlanWithHSA, isMedicareEligible = false, remainingBudget }: {
    hasMedicalPlanWithHSA: boolean;
    isMedicareEligible: boolean;
    remainingBudget: string;
  }) => {
  // Determine which banner to show
  const checkoutBannerType = useMemo(() => {
    if (
      !hasMedicalPlanWithHSA && isMedicareEligible) {
      return 'MEDICARE_THATCH_BALANCE'
    }

    if (
      !hasMedicalPlanWithHSA && !isMedicareEligible) {
      return 'THATCH_BALANCE'
    }

    if (hasMedicalPlanWithHSA) {
      return 'HSA_WARNING'
    }

    return ''
  }, [hasMedicalPlanWithHSA, isMedicareEligible, remainingBudget])

  switch (checkoutBannerType as QmeCheckoutBannerType) {
    case 'MEDICARE_THATCH_BALANCE':
      return (
        <Banner
          title='You keep the difference'
          type='notice'
          icon={LightbulbIcon}
        >
          <p data-testid='medicare-thatch-balance-banner'>
            If your plan premiums are less than {remainingBudget}, you'll
            be able to spend the remainder on other healthcare expenses
            like copays, prescriptions, or the{' '}
            <TextLink
              href='https://app.thatch.com/marketplace'
              target='_blank'
              rel='noopener noreferrer'
            >
              Thatch Marketplace
            </TextLink>
            .
          </p>
        </Banner>
      )

    case 'THATCH_BALANCE':
      return (
        <Banner
          title='You keep the difference'
          type='notice'
          icon={LightbulbIcon}
        >
          <p data-testid='thatch-balance-banner'>
            You'll get {remainingBudget} each month to spend on other
            healthcare expenses like copays, prescriptions, or the{' '}
            <TextLink
              href={membersMarketplace.index()}
              target='_blank'
              rel='noopener noreferrer'
            >
              Thatch marketplace
            </TextLink>
            .
          </p>
        </Banner>
      )

    case 'HSA_WARNING':
      return (
        <Banner
          title='Important notice about HSAs'
          type='warning'
          icon={SolidAlertCircleIcon}
        >
          <p data-testid='hsa-warning-banner'>
            You can contribute to an HSA with this plan, but{' '}
            <TextLink href={HSA_SUPPORT_URL} target='_blank' rel='noopener noreferrer'>
              won't be able to use your remaining budget
            </TextLink>
            {' '}on Thatch until you've paid $1,700 out of pocket (or $3,400 for a family).
          </p>
        </Banner>
      )

    default:
      return null
  }
}

export function AllowanceSidebar ({
  isAtFinalizeEnrollment,
  navigateToSpecificStep,
  displayInBottomSheet,
  isMemberOnTieredAllowance,
  showInNetworkVerificationProgress,
}: {
  displayInBottomSheet?: boolean;
  isAtFinalizeEnrollment: boolean;
  navigateToSpecificStep?: (step: Step) => void;
  isMemberOnTieredAllowance: boolean;
  showInNetworkVerificationProgress?: boolean;
}) {
  const { onboardingData } = useOnboarding()
  const enrollmentItems = onboardingData?.enrollments || []
  const additionalParticipants = onboardingData?.additional_participants || []

  const intercomLauncherOffset = useIntercomLauncherOffset('inline')

  const tieredAllowanceCalculation = useMemo(() => {
    if (isMemberOnTieredAllowance) {
      return calculateTieredAllowanceBreakdown({
        allowanceAmount: onboardingData?.allowance?.allowance_amount,
        medicalEnrollment: enrollmentItems[0],
      })
    }
    return null
  }, [
    isMemberOnTieredAllowance,
    onboardingData?.allowance?.allowance_amount,
    enrollmentItems,
  ])

  let remainingBudget: string
  let remainingRawAmount: number
  let remainingSubunits: number
  if (isMemberOnTieredAllowance) {
    const { formattedAmount, rawAmount, subunits } =
      tieredAllowanceCalculation!
    remainingBudget = formattedAmount
    remainingRawAmount = rawAmount
    remainingSubunits = subunits
  } else {
    const { formattedAmount, rawAmount, subunits } = calculateRemainingBudget(
      onboardingData?.allowance?.allowance_amount,
      enrollmentItems
    )
    remainingBudget = formattedAmount
    remainingRawAmount = rawAmount
    remainingSubunits = subunits
  }
  let remainingBudgetTextColor = 'text-gray-800'
  if (!isMemberOnTieredAllowance) {
    if (remainingSubunits < 0) {
      remainingBudgetTextColor = 'text-red-500'
    } else if (remainingSubunits > 0) {
      remainingBudgetTextColor = 'text-green-500'
    }
  }

  const totalPlanCosts = calculateTotalPlanCosts(enrollmentItems)

  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date

  const hasMedicalPlanWithHSA = enrollmentItems.some(
    (enrollment) => enrollment.medical_plan_hsa_eligible
  )

  const canUseQME =
    remainingSubunits > 0 &&
    onboardingData?.allowance?.qme_treatment === 'all_allowed'

  const isMedicareEligible =
    onboardingData?.restrictions?.medicare_eligible &&
    onboardingData.restrictions.medicare_eligible.length > 0

  function getBudgetLabel () {
    return isMemberOnTieredAllowance ? 'You pay' : 'Remaining budget'
  }

  function getTooltipText () {
    if (isMemberOnTieredAllowance) {
      return TIERED_ALLOWANCE_TOOLTIP_TEXT
    }
    if (onboardingData?.allowance?.qme_treatment === 'all_allowed') {
      return "Anything you don't spend on plan premiums can be used on other healthcare expenses like copays, prescriptions, and more."
    } else if (onboardingData?.allowance?.qme_treatment === 'disabled') {
      return 'Any money not spent on plan premiums will go unused.'
    } else if (onboardingData?.allowance?.qme_treatment === 'copay_only') {
      return "Anything you don't spend on plan premiums can be used to cover your provider and prescription copays throughout the year."
    }
  }

  function renderContent ({ dismissSheet }: { dismissSheet?: () => void } = {}) {
    const tooltipText = getTooltipText()
    const budgetText = getBudgetLabel()

    return (
      <>
        {!(isMemberOnTieredAllowance && isAtFinalizeEnrollment) && (
          <div className='flex flex-col gap-y-2 mb-6' data-testid='remaining-budget-container'>
            <div className='text-gray-500 body-sm flex gap-x-1 items-center'>
              <h2>
                {budgetText}
              </h2>
              {tooltipText && (
                <Tooltip content={tooltipText}>
                  <InfoIcon aria-label='Info icon' size={16} />
                </Tooltip>
              )}
            </div>
            <span aria-atomic aria-live='polite' className='sr-only'>
              {budgetText}: {remainingBudget}
            </span>
            <AnimatedCurrency
              aria-hidden
              className='text-gray-800 heading-xl'
              value={
                isMemberOnTieredAllowance && enrollmentItems.length === 0
                  ? null
                  : remainingRawAmount
              }
            />
          </div>
        )}
        <div className='flex flex-col gap-y-4 mb-10 flex-1'>
          {!isAtFinalizeEnrollment && (
            <>
              <h2 className='text-gray-500 body-sm'>Participants</h2>
              <div className='flex flex-wrap gap-x-4'>
                <div className='flex items-center gap-x-2 body-sm text-gray-800'>
                  <UserIcon className='w-3 h-3' />
                  <span>You</span>
                </div>
                {additionalParticipants?.map((participant) => (
                  <div
                    key={participant.id}
                    className='flex items-center gap-x-2 body-sm text-gray-800'
                  >
                    <UsersIcon className='w-3 h-3' />
                    <p>{participant.given_name}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          <div
            className={clsx(
              'flex flex-col gap-y-4',
              !(isMemberOnTieredAllowance && isAtFinalizeEnrollment) && 'border-t border-gray-200',
              isAtFinalizeEnrollment ? 'py-6' : 'py-10'
            )}
          >
            {isAtFinalizeEnrollment && soonestCoverageStartDate && (
              <p className='text-gray-800 body'>
                Coverage starts{' '}
                <span className='font-semibold'>
                  {getFormattedDate(soonestCoverageStartDate, {
                    month: 'long',
                    timeZone: 'UTC',
                  })}
                </span>{' '}
                for:
              </p>
            )}
            {enrollmentItems.length === 0 && (
              <p className='text-gray-500 body-sm text-center'>
                Your selected plans will appear here.
              </p>
            )}
            <div>
              {enrollmentItems.map((enrollment, index) => {
                return (
                  <EnrollmentSidebarCard
                    key={enrollment.id ?? `enrollment-${index}`}
                    enrollment={enrollment}
                    isAtFinalizeEnrollment={isAtFinalizeEnrollment}
                    navigateToSpecificStep={(step: Step) => {
                      navigateToSpecificStep?.(step)
                      dismissSheet?.()
                    }}
                  />
                )
              })}
            </div>
          </div>
          {showInNetworkVerificationProgress && (
            <>
              <div className='flex-1' />
              <ProviderInNetworkVerificationProgressCard navigateToSpecificStep={navigateToSpecificStep} />
            </>
          )}
          {isAtFinalizeEnrollment && !isMemberOnTieredAllowance && (
            <dl className='w-full grid grid-cols-2 gap-y-2 pt-6 text-gray-800 tracking-normal font-normal border-t border-gray-200'>
              <dt className='body text-nowrap'>Total budget</dt>
              <dd className='text-right body'>
                {onboardingData?.allowance?.allowance_amount?.formatted_amount}
              </dd>
              <dt className='body text-nowrap'>Total plan costs</dt>
              <dd className='text-right body' data-testid='total-plan-costs'>
                -{formatMoney(totalPlanCosts / 100)}
              </dd>
              <dt className='heading-sm text-nowrap'>Remaining budget</dt>
              <dd
                className={'text-right heading-sm ' + remainingBudgetTextColor}
                data-testid='remaining-budget'
              >
                {remainingBudget}
              </dd>
            </dl>
          )}
          {isAtFinalizeEnrollment && isMemberOnTieredAllowance && (
            <dl className='w-full grid grid-cols-2 gap-y-2 pt-6 text-gray-800 tracking-normal font-normal border-t border-gray-200'>
              <dt className='body text-nowrap'>Total cost</dt>
              <dd className='text-right body'>
                {formatMoney(totalPlanCosts / 100)}
              </dd>
              <dt className='body text-nowrap'>Your employer pays</dt>
              <dd className='text-right body' data-testid='total-plan-costs'>
                -{tieredAllowanceCalculation?.formattedEmployerContribution}
              </dd>
              <dt className='heading-sm text-nowrap'>You pay</dt>
              <dd
                className={'text-right heading-sm ' + remainingBudgetTextColor}
                data-testid='remaining-budget'
              >
                {remainingBudget}
              </dd>
            </dl>
          )}
          {isAtFinalizeEnrollment && canUseQME &&
            <QmeCheckoutBanner
              hasMedicalPlanWithHSA={hasMedicalPlanWithHSA}
              isMedicareEligible={isMedicareEligible ?? false}
              remainingBudget={remainingBudget}
            />}

        </div>
      </>
    )
  }

  if (displayInBottomSheet) {
    return (
      <div className='fixed flex justify-center' style={intercomLauncherOffset}>
        <BottomSheet
          trigger={
            <button className='shadow-default w-full px-6 py-2 rounded-full bg-gray-800 text-white body flex items-center justify-between gap-4'>
              <div className='flex justify-between gap-2 items-center grow'>
                <span className='body-sm'>{getBudgetLabel()}:</span>
                <strong className='body-bold'>
                  {isMemberOnTieredAllowance && enrollmentItems.length === 0
                    ? (
                      <>
                        $<span className='font-mono'>-.--</span>
                      </>
                      )
                    : (
                        remainingBudget
                      )}
                </strong>
              </div>
              <div>
                <ChevronUpIcon size={16} />
              </div>
            </button>
          }
        >
          {({ dismissSheet }) => (
            <div className='p-4'>{renderContent({ dismissSheet })}</div>
          )}
        </BottomSheet>
      </div>
    )
  }

  return (
    <div data-testid='AllowanceSidebar' className='pt-24 pb-10 px-6 h-full flex flex-col'>
      {renderContent()}
    </div>
  )
}
